import React, { useState, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { toast } from 'react-toastify';
import { useStore } from 'hooks';
import {
  ApplicationWagonService,
  ApplicationSupplyService,
  WagonService,
  ProfileService,
  StationService,
  ContractService,
} from 'services';
import { groupBy } from 'helpers/utils';
import { STATUS_APPLICATION_WAGON, CONTRACT_STATUS, VALIDATION } from 'helpers/constants';
import Success from './success';
import Form from './form';
import styles from '../order.module.scss';

const useInitialValues = ({
  edit,
  profile = {},
  applicationWagons = [],
}) => {
  const {
    fio = '',
    email = '',
    phone = '',
  } = profile;
  const getEditWagonField = (field, defaultValue = '') => {
    const editApplicationWagon = edit && applicationWagons
      .find(({ id }) => id === edit.applicationWagonId);

    return editApplicationWagon?.[field] ?? defaultValue;
  };

  const applicationWagon = applicationWagons.length ? applicationWagons[0] : '';
  const applicationWagonId = applicationWagon ? applicationWagon.id : '';
  const applicationWagonGy12 = applicationWagon ? applicationWagon.gy12 : '';
  const contractId = applicationWagon ? applicationWagon.contractId : '';
  const type = applicationWagon ? applicationWagon.type : '';
  const contractProductId = applicationWagon
    ? Array.isArray(applicationWagon.contractProduct)
      && applicationWagon.contractProduct.length
      && applicationWagon.contractProduct[0].id : '';
  const productGroup = applicationWagon ? applicationWagon.productGroup : '';
  const wagonId = applicationWagon ? applicationWagon.wagonId : '';
  const stationId = applicationWagon ? applicationWagon.stationId : '';
  const utensils = applicationWagon ? applicationWagon.utensils : 0;
  const codeGd = applicationWagon ? applicationWagon.codeGd : '';
  const userConsigneeId = applicationWagon.userConsigneeId === null
    ? 'default'
    : applicationWagon.userConsigneeId;

  const initialValues = {
    applicationWagonId: edit
      ? getEditWagonField('id', edit.applicationWagonId || edit.applicationWagon?.id || '')
      : applicationWagonId,
    contractId: edit ? getEditWagonField('contractId') : contractId,
    productGroup: edit ? getEditWagonField('productGroup') : productGroup,
    contractProductId: edit ? edit.contractProductId : contractProductId,
    userConsigneeId: edit ? getEditWagonField('userConsigneeId', 'default') : userConsigneeId,
    wagonId: edit ? getEditWagonField('wagonId') : wagonId,
    stationId: edit ? getEditWagonField('stationId') : stationId,
    utensils: edit ? getEditWagonField('utensils') : utensils,
    gy12: edit
      ? getEditWagonField('gy12', edit.applicationWagon?.gy12 || '')
      : applicationWagonGy12,
    codeGd: edit ? getEditWagonField('codeGd') : codeGd,
    type: edit ? getEditWagonField('type') : type,
    fio: edit ? edit.user.fio : fio,
    email: edit ? edit.user.email : email,
    phone: edit ? edit.user.phone : phone,
    ton: edit ? edit.ton : 0,
    wagonCount: edit ? edit.wagonCount : 0,
    shippedTon: edit ? edit.shippedTon : 0,
    payments: [
      edit ? edit.payments[0] : '',
      edit ? edit.payments[1] : '',
    ],
    additional: edit ? edit.additional : '',
    comment: edit ? edit.comment : '',
    file: null,
  };

  if (edit) {
    initialValues.id = edit.id;
    initialValues.status = edit.status;
    initialValues.statusComment = '';
  }

  return initialValues;
};

const useFetch = ({ id, userId } = {}) => {
  const { setUser, setLoading } = useStore();
  const [tons, setTons] = useState([]);
  const [wagons, setWagons] = useState([]);
  const [profile, setProfile] = useState({});
  const [stations, setStations] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [applicationWagons, setApplicationWagons] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);

      const wagonResponse = await new WagonService({ setUser }).getWagons();
      if (wagonResponse) {
        setWagons(wagonResponse);
      }

      const profileResponse = await new ProfileService({ setUser }).getProfile({
        allContract: 1,
        userId,
      });
      if (profileResponse) {
        setProfile(profileResponse);
      }

      const tonsResponse = await new ContractService({ setUser }).getProductsTons({ userId });
      if (tonsResponse) {
        setTons(tonsResponse);
      }

      const stationResponse = await new StationService({ setUser }).getStations();
      if (stationResponse) {
        setStations(stationResponse);
      }

      const applicationWagonsResponse = await new ApplicationWagonService({ setUser })
        .getAgreedApplications({ id, userId });
      if (applicationWagonsResponse) {
        setApplicationWagons(applicationWagonsResponse);
      }

      if (id) {
        const statusHistoryResponse = await new ApplicationSupplyService({ setUser })
          .getStatusHistory({ id });
        if (statusHistoryResponse) {
          setStatusHistory(statusHistoryResponse);
        }
      }

      setLoading(false);
    })();
  }, [id, userId, setUser, setLoading]);

  return [tons, wagons, profile, stations, statusHistory, applicationWagons];
};

const Supply = ({ edit, onClose }) => {
  const [tons, wagons, profile, stations, statusHistory, applicationWagonsData] = useFetch(edit);
  const applicationWagons = edit
    ? applicationWagonsData
    : applicationWagonsData.filter(({ status, contract }) => (
      status === STATUS_APPLICATION_WAGON.AGREED
      && Number(contract?.status) === CONTRACT_STATUS.performing
    ));

  const initialValues = useInitialValues({ edit, wagons, stations, profile, applicationWagons });
  const { setUser, setLoading } = useStore();
  const [order, setOrder] = useState(null);
  const isSubmittingRef = useRef(false);
  const { contracts = [], userConsignees = [] } = profile;

  const getIndex = (data, value) => data.findIndex(({ id }) => id === Number(value));

  const applyApplicationWagonToFields = (applicationWagon, setFieldValue) => {
    if (!applicationWagon) {
      return;
    }

    const contractProductId = Array.isArray(applicationWagon.contractProduct)
      && applicationWagon.contractProduct.length
      ? applicationWagon.contractProduct[0].id
      : '';

    const {
      id,
      wagonId,
      utensils,
      stationId,
      contractId,
      userConsigneeId,
      productGroup,
      gy12,
      codeGd,
      type,
    } = applicationWagon;

    setFieldValue('applicationWagonId', id);
    setFieldValue('ton', 0);
    setFieldValue('type', type);
    setFieldValue('gy12', gy12);
    setFieldValue('codeGd', codeGd);
    setFieldValue('wagonId', wagonId);
    setFieldValue('utensils', utensils);
    setFieldValue('stationId', stationId);
    setFieldValue('contractId', contractId);
    setFieldValue('userConsigneeId', userConsigneeId === null ? 'default' : userConsigneeId);
    setFieldValue('contractProductId', contractProductId);
    setFieldValue('productGroup', productGroup);
  };

  const onChangeApplicationWagon = ({ target: { value } }, setFieldValue) => {
    const applicationWagonIndex = getIndex(applicationWagons, value);
    const applicationWagon = applicationWagons[applicationWagonIndex];
    applyApplicationWagonToFields(applicationWagon, setFieldValue);
  };

  const onChangeProduct = (setFieldValue) => {
    setFieldValue('ton', 0);
  };

  const getCountWagon = ({ wagonId, ton }) => {
    const wagonCurrent = groupBy(wagons, 'id')[wagonId];
    return Array.isArray(wagonCurrent) && wagonCurrent.length
      ? Math.round(ton / wagonCurrent[0].ton)
      : 0;
  };

  const applicationSupplyService = new ApplicationSupplyService({ setUser });

  const onSubmit = async (values) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    const formData = new FormData();
    const keys = Object.keys(values);

    keys.forEach((key) => {
      const item = values[key];
      switch (key) {
        case 'wagonCount':
          formData.append(key, getCountWagon(values));
          break;
        case 'payments':
          item.forEach(value => formData.append(key, value));
          break;
        default:
          formData.append(key, item);
          break;
      }
    });

    setLoading(true);

    try {
      const response = await applicationSupplyService.postApplication(formData);
      if (response && response.id) {
        setOrder(response);
      }
    } finally {
      isSubmittingRef.current = false;
      setLoading(false);
    }
  };

  const onUpdateTon = async ({ id, ton, shippedTon }) => {
    const tonValue = Number(ton);
    const shippedTonValue = Number(shippedTon);

    if (!Number.isNaN(tonValue) && !Number.isNaN(shippedTonValue) && shippedTonValue > tonValue) {
      toast.error(...VALIDATION.SHIPPED_TON_EXCEEDS_TON);
      return;
    }

    setLoading(true);
    try {
      return await applicationSupplyService.updateTon({ id, ton, shippedTon });
    } finally {
      setLoading(false);
    }
  };

  const onUpdateGy12 = async ({ applicationWagonId, gy12 }) => {
    const applicationWagon = applicationWagons.find(({ id }) => (
      Number(id) === Number(applicationWagonId)
    ));
    const id = applicationWagonId
      || edit?.applicationWagonId
      || edit?.applicationWagon?.id
      || applicationWagon?.id;

    if (!id) {
      toast.error('Не удалось определить заявку на вагоны');
      return null;
    }

    setLoading(true);
    try {
      return await new ApplicationWagonService({ setUser }).updateGy12({
        id,
        gy12,
      });
    } finally {
      setLoading(false);
    }
  };

  const onUpdateStatus = async (values) => {
    const formData = new FormData();
    const keys = Object.keys(values);

    keys.forEach((key) => {
      const item = values[key];
      switch (key) {
        case 'id':
        case 'status':
        case 'file':
          formData.append(key, item);
          break;
        case 'statusComment':
          formData.append('comment', item);
          break;
        default:
      }
    });

    setLoading(true);
    try {
      const response = await applicationSupplyService.changeStatus(formData);
      if (response) {
        toast.success('Заявка обновлена');
      }
      return response;
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={classnames(styles.order, { [styles.success]: order !== null })}>
      {order ? (
        <Success
          order={order}
          onClose={onClose}
        />
      ) : (
        <Form
          edit={edit}
          tons={tons}
          wagons={wagons}
          profile={profile}
          stations={stations}
          contracts={contracts}
          statusHistory={statusHistory}
          applicationWagons={applicationWagons}
          userConsignees={userConsignees}
          initialValues={initialValues}
          onClose={onClose}
          onSubmit={onSubmit}
          onUpdateTon={onUpdateTon}
          onUpdateGy12={onUpdateGy12}
          onChangeProduct={onChangeProduct}
          onUpdateStatus={onUpdateStatus}
          onChangeApplicationWagon={onChangeApplicationWagon}
          getCountWagon={getCountWagon}
          getIndex={getIndex}
        />
      )}
    </section>
  );
};

export default Supply;
